import { useMutation, useQueryClient } from '@tanstack/react-query';
import { restCacheConfig } from '../config/axios';
import { QUERY_KEYS } from '../hooks/hooksUtils';
import { useInfiniteQueryData } from '../hooks/useInfiniteQueryData';
import { useQueryData } from '../hooks/useQueryData';
import { useUserStore } from '../stores/UserStore';
import { RestService } from './RestService';
import { MULTIPART_HEADERS } from './Utils';

export const defaultAppsConfig = {
  projects: true,
  assets: true,
  forms: true,
  pages: true,
  documents: true,
  leaves: true,
  sales: true,
  purchases: true
};

export const BUSINESS_LIMITS = {
  businesses: 5,
  projects: 10,
  messages: 1000,
  tasks: 100,
  assets: 100,
  pages: 100,
  leaves: 100,
  forms: 100,
  documents: 100,
  clients: 100,
  quotations: 100
};

export const fetchBusinesses = ({ pageParam }) => {
  return RestService.get(`/business?page=${pageParam}`);
};

export const useBusinessesData = () => {
  return useInfiniteQueryData([QUERY_KEYS.BUSINESSES], fetchBusinesses, restCacheConfig);
};

export const fetchBusiness = ({ queryKey }: any) => {
  return RestService.get(`/business/` + queryKey[1]);
};

export const useBusinessData = (uuid: string) => {
  return useQueryData([QUERY_KEYS.BUSINESSES, uuid], fetchBusiness, restCacheConfig);
};

export const useAddBusinessMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (business: any) => RestService.post('/business', business),
    onSuccess: async newData => {
      const oldData1 = queryClient.getQueryData([QUERY_KEYS.BUSINESSES]);
      if (oldData1) {
        queryClient.setQueryData([QUERY_KEYS.BUSINESSES], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            pages: oldQueryData.pages.map((pageData: any, index: number) => {
              return {
                ...pageData,
                data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
              };
            })
          };
        });
      }
    }
  });
};

export const useUpdateBusinessMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ business, formData }: any) =>
      RestService.update('/business/' + business.uuid, formData || business, formData && MULTIPART_HEADERS),
    onSuccess: async (newData, { business }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.BUSINESSES, business.uuid] });
      const oldData = queryClient.getQueryData([QUERY_KEYS.BUSINESSES, business.uuid]);
      if (oldData) {
        queryClient.setQueryData([QUERY_KEYS.BUSINESSES, business.uuid], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: newData.data
          };
        });
      }
      const oldData1 = queryClient.getQueryData([QUERY_KEYS.BUSINESSES]);
      if (oldData1) {
        queryClient.setQueryData([QUERY_KEYS.BUSINESSES], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            pages: oldQueryData.pages.map((pageData: any) => {
              return {
                ...pageData,
                data: pageData.data.map(item => {
                  if (item.uuid === newData.data.uuid) {
                    return newData.data;
                  }
                  return item;
                })
              };
            })
          };
        });
      }
      return { oldData };
    }
  });
};

export const useDeleteBusinessAccountMutation = () => {
  const queryClient = useQueryClient();
  const businessUuid = useUserStore.getState().UBUUID;
  return useMutation({
    mutationFn: () => RestService.update('/business/delete-account/' + businessUuid, {}),
    onSuccess: async newData => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.BUSINESSES, businessUuid] });
      const oldData = queryClient.getQueryData([QUERY_KEYS.BUSINESSES, businessUuid]);
      if (oldData) {
        queryClient.setQueryData([QUERY_KEYS.BUSINESSES, businessUuid], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: newData.data
          };
        });
      }
    }
  });
};

export const useDeleteBusinessMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/business/' + uuid),
    onSuccess: async (newData, uuid) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.BUSINESSES] });
      const oldData = queryClient.getQueryData([QUERY_KEYS.BUSINESSES]);
      if (oldData) {
        queryClient.setQueryData([QUERY_KEYS.BUSINESSES], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            pages: oldQueryData.pages.map((pageData: any) => {
              return {
                ...pageData,
                data: pageData.data.filter(item => item.uuid !== uuid)
              };
            })
          };
        });
      }
    }
  });
};
